import { useCallback, useEffect, useId, useRef, useState, type ComponentType, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import Image from 'next/image';
import { buildClassName } from 'utils/build-class-name';
import {
  Accessibility,
  Briefcase,
  Building,
  ChevronDown,
  ClipboardList,
  Globe,
  Handshake,
  Heart,
  Languages,
  List,
  Medal,
  Search,
  SquareUser,
  TrendingUp,
  UserKey,
  UserSearch,
  ChevronRight,
  DollarSign,
  House,
  Info,
  KeyRound,
  LogOut,
  Mail,
  Map,
  Menu,
  Newspaper,
  User,
  type LucideProps,
} from 'lucide-react';
import type {
  DropdownNavigationButton,
  LinkMenuItem,
  MenuItem,
  NavigationButton,
  NavigationConfig,
  NavigationActionHandler,
} from '@core/ui/navigation';
import type { User as AuthUser } from '@bedrock/auth';
import { useChat, useModalContext, useUserContext, LOGIN_REGISTRATION_MODAL } from 'contexts';
import { INTERNATIONAL_LINKS_MODAL } from 'contexts/modal';
import type { InternationalLinksOptions } from 'components/modal-dialog/international-links';
import { GLOBAL_PRESENCE_ITEM_ID, OPEN_INTERNATIONAL_LINKS_HANDLER } from './use-rail-menu-config';

import type { RailLocaleMenu } from './use-rail-menu-config';
import { useLocationResolution } from 'hooks/use-location-resolution';
import useOutsideClickHandler from 'hooks/use-outside-click-handler';
import { openEqualWebMenu } from 'utils/accessibility';
import { processNavigationItemsWithLocation } from 'utils/navigation-item-processor';
import styles from './rail-menu.module.scss';

export type RailMenuProps = {
  navConfig: NavigationConfig;
  /** Renders the trigger as an icon-only circular button, opening a fullscreen menu instead of the desktop flyout — for the mobile bottom nav bar instead of the vertical desktop rail. */
  mobileVariant?: boolean;
  /** Tenant logo shown at the top of the mobile fullscreen menu. Unused on desktop (`LeftRail` renders its own logo). */
  logoSrc?: string;
  logoAlt?: string;
  /** Renders the enlarged eXp lockup treatment instead of the compact Zoocasa wordmark. */
  isExpLogo?: boolean;
  /** Accessible label for the menu trigger button and its visible text. */
  menuLabel?: string;
  /** Called whenever the menu (desktop flyout or mobile fullscreen sheet) opens or closes. */
  onOpenChange?: (isOpen: boolean) => void;
  /** Adds a "Language" entry in the mobile hamburger only (desktop uses the rail toggle). */
  localeMenu?: RailLocaleMenu;
};

const PROFILE_HREF = '/account/details'; // TODO: Confirm if there is a new profile page.

const LANGUAGE_ITEM_ID = 'language';
const CHANGE_LOCALE_HANDLER = 'changeLocale';

// The wide eXp Realty lockup needs a larger box than the compact Zoocasa wordmark
// to stay legible — same aspect ratios as `LeftRail`'s sizing for these logo assets,
// scaled up for the fullscreen mobile menu's header.
const EXP_LOGO_WIDTH = 80;
const EXP_LOGO_HEIGHT = 43;
const DEFAULT_LOGO_WIDTH = 70;
const DEFAULT_LOGO_HEIGHT = 31;

const MENU_ICONS: Record<string, ComponentType<LucideProps>> = {
  buy: House,
  rent: KeyRound,
  sell: DollarSign,
  'market-insights': Newspaper,
  newsletter: Mail,
  careers: Briefcase,
  'about-us': Info,
  accessibility: Accessibility,
  sitemap: Map,
  login: User,
  [GLOBAL_PRESENCE_ITEM_ID]: Globe,
  [LANGUAGE_ITEM_ID]: Languages,
  // eXp tenants' header-derived rail items. The eXp Realty set below is the design-approved
  // mapping; the remaining entries are still placeholders.
  search: Search,
  'find-an-agent': UserSearch,
  'join-exp': Handshake,
  'exp-agent-login': SquareUser,
  'agent-login': UserKey,
  about: Info,
  'commercial-waitlist': Building,
  // eXp Commercial rail items (placeholders).
  'find-an-expert': UserSearch,
  join: Handshake,
  'our-services': ClipboardList,
  insights: TrendingUp,
  listings: List,
  // eXp In-Service rail items (placeholders).
  'header-buy-button': House,
  'header-find-agent-button': UserSearch,
  'header-join-exp-button': Handshake,
  'header-sitemap-button': Map,
  'military-rewards-program-button': Medal,
  'in-service-life-button': Heart,
};

/** Get User's initials to display on default profile avatar */
function getUserInitials(user: AuthUser | null): string {
  const first = user?.firstName?.trim()?.[0] ?? '';
  const last = user?.lastName?.trim()?.[0] ?? '';
  const initials = `${first}${last}`.toUpperCase();
  return initials || 'U';
}

function MenuItemIcon({ itemId }: { itemId: string }) {
  const Icon = MENU_ICONS[itemId];
  if (!Icon) return null;
  return (
    <span className={styles.itemIconChip} aria-hidden>
      <Icon className={styles.itemIcon} strokeWidth={2} />
    </span>
  );
}

function withoutDividers(items: MenuItem[]): MenuItem[] {
  return items
    .filter(item => item.type !== 'divider')
    .map(item => (
      item.type === 'expandable'
        ? { ...item, items: withoutDividers(item.items) }
        : item
    ));
}

function headerDropdownsToMenuItems(headerItems: NavigationButton[]): MenuItem[] {
  return headerItems
    .filter((item): item is DropdownNavigationButton => item.buttonVariant === 'dropdown')
    .map(item => ({
      id: item.id,
      type: 'expandable' as const,
      label: item.label || item.id,
      items: withoutDividers(item.items),
    }));
}

function isLinkMenuItem(item: MenuItem): item is LinkMenuItem | Extract<MenuItem, { type: 'action' }> {
  return item.type === 'link' || item.type === 'action';
}

type ExpandableMenuItem = Extract<MenuItem, { type: 'expandable' }>;

function isExpandableMenuItem(item: MenuItem): item is ExpandableMenuItem {
  return item.type === 'expandable';
}

// Nav metadata params are untyped by contract, so validate before handing the
// payload to the international-links modal.
function isInternationalLinksOptions(value: unknown): value is InternationalLinksOptions {
  return (
    typeof value === 'object' && value !== null
    && 'title' in value && typeof value.title === 'string'
    && 'subtitle' in value && typeof value.subtitle === 'string'
    && 'linkData' in value && Array.isArray(value.linkData)
  );
}

type MenuAnchorProps = {
  item: MenuItem;
  className: string;
  testId: string;
  onAfterNavigate?: () => void;
  onAction: NavigationActionHandler;
  children: ReactNode;
};

/** Menu item as link or button (side dropdown) for left rail menu */
function MenuAnchor({ item, className, testId, onAfterNavigate, onAction, children }: MenuAnchorProps) {
  if (!isLinkMenuItem(item)) {
    return null;
  }

  if (item.metadata?.handler) {
    return (
      <button
        type="button"
        className={className}
        data-testid={testId}
        aria-label={item.label}
        onClick={() => {
          onAction(item.metadata.handler, item.metadata.params);
          onAfterNavigate?.();
        }}
      >
        {children}
      </button>
    );
  }

  const href = item.href || '#';
  return (
    <a
      href={href}
      className={className}
      data-testid={testId}
      aria-label={item.label}
      target={item.external ? '_blank' : undefined}
      rel={item.external ? 'noopener noreferrer' : undefined}
      onClick={() => onAfterNavigate?.()}
    >
      {children}
    </a>
  );
}

export function RailMenu({ navConfig, mobileVariant = false, logoSrc, logoAlt, isExpLogo = false, menuLabel = 'Menu', onOpenChange, localeMenu }: RailMenuProps) {
  const { isCrawler, isAuthenticated, user, signOut } = useUserContext();
  const { openModal } = useModalContext();
  const { openChat } = useChat();
  const resolvedLocation = useLocationResolution();
  const flyoutId = useId();
  const flyoutRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(isCrawler);
  const [isPinned, setIsPinned] = useState(false);
  const [activeSubmenuId, setActiveSubmenuId] = useState<string | null>(null);
  const [submenuOffsetTop, setSubmenuOffsetTop] = useState(0);

  const closeMenu = useCallback(() => {
    if (isCrawler) return;
    setIsOpen(false);
    setIsPinned(false);
    setActiveSubmenuId(null);
  }, [isCrawler]);

  const openSubmenu = useCallback((itemId: string, rowEl: HTMLElement) => {
    setActiveSubmenuId(itemId);
    const flyoutEl = flyoutRef.current;
    if (!flyoutEl) return;
    const flyoutRect = flyoutEl.getBoundingClientRect();
    const rowRect = rowEl.getBoundingClientRect();
    setSubmenuOffsetTop(rowRect.top - flyoutRect.top);
  }, []);

  const wrapRef = useOutsideClickHandler<HTMLDivElement>(isOpen && !isCrawler ? closeMenu : undefined);

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    if (!isOpen || isCrawler) return undefined;

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, isCrawler, closeMenu]);

  const handleNavigationAction = useCallback<NavigationActionHandler>((handlerName, params) => {
    switch (handlerName) {
      case 'openAccessibilityMenu':
        openEqualWebMenu();
        break;
      case OPEN_INTERNATIONAL_LINKS_HANDLER:
        if (isInternationalLinksOptions(params?.links)) {
          openModal(INTERNATIONAL_LINKS_MODAL, params.links);
        }
        break;
      case CHANGE_LOCALE_HANDLER:
        if (typeof params?.locale === 'string') {
          localeMenu?.onSelect(params.locale);
        }
        break;
      case 'openChat':
        openChat({ source: 'Menu' });
        break;
      case 'login':
        openModal(LOGIN_REGISTRATION_MODAL);
        break;
      case 'signOut':
        void signOut();
        break;
      default:
        console.warn(`Unknown navigation action: ${handlerName}`);
    }
  }, [openChat, openModal, signOut, localeMenu]);

  const buyRentSellItems = processNavigationItemsWithLocation(
    headerDropdownsToMenuItems(navConfig.structure.header),
    resolvedLocation
  );
  const secondaryItems = withoutDividers(
    processNavigationItemsWithLocation(
      navConfig.structure.menu,
      resolvedLocation
    )
  );
  // On Mobile, language switcher shows in the hamburger menu
  const languageItem: ExpandableMenuItem | null = mobileVariant && localeMenu
    ? {
      id: LANGUAGE_ITEM_ID,
      type: 'expandable',
      label: localeMenu.label,
      items: localeMenu.options.map(option => ({
        id: `locale-${option.id}`,
        type: 'link' as const,
        label: option.label,
        href: '#',
        metadata: { handler: CHANGE_LOCALE_HANDLER, params: { locale: option.id }},
      })),
    }
    : null;
  const flyoutItems = [...buyRentSellItems, ...secondaryItems, ...(languageItem ? [languageItem] : [])];
  const activeExpandable = !isCrawler && activeSubmenuId
    ? flyoutItems.find((item): item is ExpandableMenuItem => (
      isExpandableMenuItem(item) && item.id === activeSubmenuId
    ))
    : undefined;
  const activeSubmenuIdForAria = activeExpandable ? `${flyoutId}-${activeExpandable.id}` : undefined;
  const activeNestedLinks = activeExpandable?.items.filter(isLinkMenuItem) ?? [];

  const openPrimary = useCallback(() => setIsOpen(true), []);

  const onWrapKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && isOpen && !isCrawler) {
      event.stopPropagation();
      closeMenu();
      const trigger = wrapRef.current?.querySelector<HTMLButtonElement>('[data-testid="AreaPageV2-Rail-Menu"]');
      trigger?.focus();
    }
  }, [isOpen, isCrawler, closeMenu, wrapRef]);

  return (
    <div
      className={buildClassName(styles.wrap, mobileVariant && styles.wrapMobile)}
      ref={wrapRef}
      onKeyDown={onWrapKeyDown}
      onMouseLeave={() => {
        if (!isCrawler && !isPinned) closeMenu();
      }}
    >
      <button
        type="button"
        className={styles.navButton}
        aria-label={menuLabel}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls={isOpen ? flyoutId : undefined}
        data-testid="AreaPageV2-Rail-Menu"
        // Touch taps synthesize a mouseenter/focus immediately before the click, which
        // would otherwise open-then-immediately-toggle-closed on the very same tap.
        // Hover/focus pre-opening is a desktop-only affordance, so skip it for the
        // mobile trigger and rely purely on click to toggle open/closed there.
        onMouseEnter={mobileVariant ? undefined : openPrimary}
        onFocus={mobileVariant ? undefined : openPrimary}
        onClick={() => {
          if (isCrawler) return;
          setIsPinned(prev => {
            const nextPinned = !prev;
            if (nextPinned) {
              setIsOpen(true);
            } else {
              closeMenu();
            }
            return nextPinned;
          });
        }}
      >
        <span className={styles.trigger}>
          <Menu className={styles.icon} strokeWidth={1.9} aria-hidden />
          <span className={styles.label}>{menuLabel}</span>
        </span>
      </button>

      {isOpen && renderMenuFlyout()}
    </div>
  );

  function renderMenuFlyout() {
    const flyout = (
      <div
        id={flyoutId}
        ref={flyoutRef}
        className={mobileVariant ? styles.fullscreenMenu : styles.flyout}
        data-testid="AreaPageV2-Rail-MenuFlyout"
      >
        {mobileVariant && (
          <div className={styles.fullscreenHeaderWrap}>
            <div className={styles.fullscreenHeader}>
              <Link href="/" className={styles.fullscreenLogoLink} aria-label={logoAlt}>
                <Image
                  src={logoSrc ?? ''}
                  alt={logoAlt ?? ''}
                  className={styles.fullscreenLogo}
                  width={isExpLogo ? EXP_LOGO_WIDTH : DEFAULT_LOGO_WIDTH}
                  height={isExpLogo ? EXP_LOGO_HEIGHT : DEFAULT_LOGO_HEIGHT}
                  priority
                />
              </Link>
            </div>
          </div>
        )}
        <div className={mobileVariant ? styles.fullscreenContent : undefined}>
          <ul className={styles.menuList}>
            {flyoutItems.map(item => {
              if (item.type === 'expandable') {
                const isActive = isCrawler || activeSubmenuId === item.id;
                const nestedLinks = item.items.filter(isLinkMenuItem);
                const submenuId = `${flyoutId}-${item.id}`;

                if (isCrawler) {
                  return (
                    <li key={item.id} className={styles.crawlerExpandable}>
                      <div className={styles.menuItem} data-testid={`${item.id}MenuDropdownOption`}>
                        <MenuItemIcon itemId={item.id} />
                        <span className={styles.itemLabel}>{item.label}</span>
                      </div>
                      <ul className={styles.crawlerNestedList}>
                        {nestedLinks.map(nested => (
                          <li key={nested.id}>
                            <MenuAnchor
                              item={nested}
                              className={styles.subMenuItem}
                              testId={`${nested.id}MenuDropdownOption`}
                              onAction={handleNavigationAction}
                            >
                              {nested.label}
                            </MenuAnchor>
                          </li>
                        ))}
                      </ul>
                    </li>
                  );
                }

                // Mobile fullscreen menu expands nested links inline (accordion), since
                // there's no room for the desktop's side-by-side sub-panel.
                if (mobileVariant) {
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        className={`${styles.menuItem}${isActive ? ` ${styles.menuItemActive}` : ''}`}
                        aria-expanded={isActive}
                        aria-haspopup="true"
                        aria-controls={isActive ? submenuId : undefined}
                        aria-label={item.label}
                        data-testid={`${item.id}MenuDropdownOption`}
                        onClick={() => setActiveSubmenuId(prev => (prev === item.id ? null : item.id))}
                      >
                        <MenuItemIcon itemId={item.id} />
                        <span className={styles.itemLabel}>{item.label}</span>
                        <ChevronDown
                          className={buildClassName(styles.chevronDown, isActive && styles.chevronDownOpen)}
                          strokeWidth={2}
                          aria-hidden
                        />
                      </button>
                      {isActive && (
                        <ul id={submenuId} className={styles.accordionList}>
                          {nestedLinks.map(nested => (
                            <li key={nested.id}>
                              <MenuAnchor
                                item={nested}
                                className={styles.subMenuItem}
                                testId={`${nested.id}MenuDropdownOption`}
                                onAction={handleNavigationAction}
                                onAfterNavigate={closeMenu}
                              >
                                {nested.label}
                              </MenuAnchor>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                }

                return (
                  <li
                    key={item.id}
                    className={styles.expandableRow}
                    onMouseEnter={event => openSubmenu(item.id, event.currentTarget)}
                  >
                    <button
                      type="button"
                      className={`${styles.menuItem}${isActive ? ` ${styles.menuItemActive}` : ''}`}
                      aria-expanded={isActive}
                      aria-haspopup="true"
                      aria-controls={isActive ? submenuId : undefined}
                      aria-label={item.label}
                      data-testid={`${item.id}MenuDropdownOption`}
                      onClick={event => {
                        if (activeSubmenuId === item.id) {
                          setActiveSubmenuId(null);
                          return;
                        }
                        openSubmenu(item.id, event.currentTarget.closest('li') ?? event.currentTarget);
                      }}
                    >
                      <MenuItemIcon itemId={item.id} />
                      <span className={styles.itemLabel}>{item.label}</span>
                      <ChevronRight className={styles.chevron} strokeWidth={2} aria-hidden />
                    </button>
                  </li>
                );
              }

              if (!isLinkMenuItem(item)) return null;

              return (
                <li key={item.id} onMouseEnter={mobileVariant ? undefined : () => setActiveSubmenuId(null)}>
                  <MenuAnchor
                    item={item}
                    className={styles.menuItem}
                    testId={`${item.id}MenuDropdownOption`}
                    onAction={handleNavigationAction}
                    onAfterNavigate={closeMenu}
                  >
                    <MenuItemIcon itemId={item.id} />
                    <span className={styles.itemLabel}>{item.label}</span>
                  </MenuAnchor>
                </li>
              );
            })}
          </ul>

          {/* Account menu items (ie. profile, sign in/out) */}
          <div
            className={styles.authFooter}
            role="group"
            aria-label="Account"
            onMouseEnter={mobileVariant ? undefined : () => setActiveSubmenuId(null)}
          >
            {isAuthenticated ? (
              <>
                <a
                  href={PROFILE_HREF}
                  className={styles.menuItem}
                  aria-label="Profile"
                  data-testid="profileMenuDropdownOption"
                  onClick={() => closeMenu()}
                >
                  <span className={styles.avatarChip} aria-hidden>
                    {getUserInitials(user)}
                  </span>
                  <span className={styles.itemLabel}>Profile</span>
                </a>
                <button
                  type="button"
                  className={`${styles.menuItem} ${styles.signOutItem}`}
                  aria-label="Sign Out"
                  data-testid="sign-outMenuDropdownOption"
                  onClick={() => {
                    handleNavigationAction('signOut');
                    closeMenu();
                  }}
                >
                  <span className={styles.signOutIconChip} aria-hidden>
                    <LogOut className={styles.itemIcon} strokeWidth={2} />
                  </span>
                  <span className={styles.itemLabel}>Sign Out</span>
                </button>
              </>
            ) : (
              <button
                type="button"
                className={styles.menuItem}
                aria-label="Login"
                data-testid="loginMenuDropdownOption"
                onClick={() => {
                  handleNavigationAction('login');
                  closeMenu();
                }}
              >
                <MenuItemIcon itemId="login" />
                <span className={styles.itemLabel}>Login</span>
              </button>
            )}
          </div>
        </div>

        {/* comment: render beside the flyout so list overflow cannot clip nested panels */}
        {!mobileVariant && activeExpandable && (
          <div
            id={activeSubmenuIdForAria}
            className={styles.subPanel}
            style={{ top: submenuOffsetTop }}
            data-testid={`AreaPageV2-Rail-Submenu-${activeExpandable.id}`}
            onMouseEnter={() => setActiveSubmenuId(activeExpandable.id)}
          >
            <ul className={styles.subMenuList}>
              {activeNestedLinks.map(nested => (
                <li key={nested.id}>
                  <MenuAnchor
                    item={nested}
                    className={styles.subMenuItem}
                    testId={`${nested.id}MenuDropdownOption`}
                    onAction={handleNavigationAction}
                    onAfterNavigate={closeMenu}
                  >
                    {nested.label}
                  </MenuAnchor>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );

    // The mobile fullscreen sheet is portaled to `document.body` — `MobileNavBar`'s
    // floating pill has its own `transform` (for centering), which would otherwise
    // clip/confine this `position: fixed` overlay to the pill's own small box instead
    // of the viewport. Crawlers get the plain inline render so the links stay in the
    // server-rendered HTML rather than depending on a client-only portal.
    if (mobileVariant && !isCrawler && typeof document !== 'undefined') {
      return createPortal(flyout, document.body);
    }
    return flyout;
  }
}
